import { APIDisplayNames } from "./variables";

const defaultGraphQLQueryExample = {
    displayName: "Select Example Query",
    query:
`# SIX GraphQL Explorer lets you write, validate, and test GraphQL queries
# against SIX Web APIs
#
# Steps:
#
#   1. Install the certificate in the browser
#
#   2. Type your own query, or
#          
#       Select a sample query from the below examples
#
#   3. Run the query using the "Run request" button.
#
# Keyboard shortcuts:
#
#            Undo:  Ctrl-Z
#
#       Run query:  Ctrl-Enter (or use the "Run request" button)
#
`};

const endOfDayHistoryGraphQLQueryExample = {
    displayName: APIDisplayNames.EndOfDayHistory,
    query:
`query EndOfDayHistory {
    listings (
        scheme: TICKER_BC,
        ids: ["AAPL_67"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            listingShortName
            marketShortName
            listingCurrency
            listingStatus
        }
        marketData {
            endOfDayHistory (
                filters: {
                    dateFrom: "2022-11-01"
                }
            ) {
                sessionDate
                open
                close
                low
                high
                volume
                volumeTotal
                turnover
                turnoverTotal
                yieldToMaturity
                settlementPrice
                openInterest
                vwap
                dividend
                dividendYield
            }
        }
    }
}`};

const streamingMarketDataGraphQLQueryExample = {
    displayName: APIDisplayNames.StreamingMarketData,
    query:
`subscription StreamingMarketData {
    startStream (
        scheme: TICKER_BC,
        ids: ["AAPL_67"]
    ) {
        type
        requestedId
        requestedScheme
        last {
          value
          size
          timestamp
        }
        bestBid {
          value
          size
          timestamp
        }
        bestAsk {
          value
          size
          timestamp
        }
        spreadBestBidAsk {
          value
          timestamp
        }
        percentageSpreadBestBidAsk {
          value
          timestamp
        }
        open {
          value
          timestamp
        }
        high {
          value
          timestamp
        }
        low {
          value
          timestamp
        }
        close {
          value
          timestamp
        }
        highStaticLimit {
          value
          timestamp
        }
        lowStaticLimit {
          value
          timestamp
        }
        mid {
          value
          timestamp
        }
        numberOfTrades {
          value
          timestamp
        }
        openInterest {
          value
          timestamp
        }
        settlementPrice {
          value
          timestamp
        }
        yieldToMaturity {
          value
          timestamp
        }
        dividendYield {
          value
          timestamp
        }
        marketCapitalisation {
          value
          timestamp
        }
        volume {
          value
          timestamp
        }
        volumeTotal {
          value
          timestamp
        }
        turnover {
          value
          timestamp
        }
        turnoverTotal {
          value
          timestamp
        }
        vwap {
          value
          timestamp
        }
        priceEarningsRatioReported {
          value
          timestamp
        }
        priceEarningsRatioEstimated {
          value
          timestamp
        }
        delta {
          value
          timestamp
        }
        vega {
          value
          timestamp
        }
        gamma {
          value
          timestamp
        }
        theta {
          value
          timestamp
        }
        rho {
          value
          timestamp
        }
        omega {
          value
          timestamp
        }
        gearing {
          value
          timestamp
        }
        moneyness {
          value
          timestamp
        }
        breakEvenPoint {
          value
          timestamp
        }
        intrinsicValue {
          value
          timestamp
        }
        auctionTheoreticalMatchingPrice {
          value
          timestamp
        }
        impliedVolatility {
          value
          timestamp
        }
        historicalVolatility30Days {
          value
          timestamp
        }
        historicalVolatility90Days {
          value
          timestamp
        }
        historicalVolatility180Days {
          value
          timestamp
        }
        historicalVolatility250Days {
          value
          timestamp
        }
        closeYear {
          value
          timestamp
        }
        duration {
          value
          timestamp
        }
        modifiedDuration {
          value
          timestamp
        }
        convexity {
          value
          timestamp
        }
        accruedInterest {
          value
          timestamp
        }
        timeToMaturity {
          value
          timestamp
        }
        yieldToCall {
          value
          timestamp
        }
        totalExpenseRatio {
          value
          timestamp
        }
        percentageChangeYearToDate {
          value
          timestamp
        }
        high1Week {
          value
          timestamp
        }
        high4Week {
          value
          timestamp
        }
        high12Week {
          value
          timestamp
        }
        high26Week {
          value
          timestamp
        }
        high52Week {
          value
          timestamp
        }
        low1Week {
          value
          timestamp
        }
        low4Week {
          value
          timestamp
        }
        low12Week {
          value
          timestamp
        }
        low26Week {
          value
          timestamp
        }
        low52Week {
          value
          timestamp
        }
        highYearToDate {
          value
          timestamp
        }
        lowYearToDate {
          value
          timestamp
        }
    }
}`};

const intradayHistoryGraphQLQueryExample = {
    displayName: APIDisplayNames.IntradayHistory,
    query:
`query IntradayHistoryTrades {
    listings (
        scheme: TICKER_BC,
        ids: ["AAPL_67"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
          listingShortName
          marketShortName
          listingCurrency
          listingStatus
        }
        marketData {
            intradayHistory {
                trades (
                    filters: {
                        dateFrom: "2023-11-15",
                        timeFrom: "08:45:15",
                        dateTo: "2023-11-16",
                        timeTo: "10:45:20"
                    }
                ) {
                    timestamp
                    trade {
                        value
                        size
                    }
                    bestBid {
                        value
                        size
                    }
                    bestAsk {
                        value
                        size
                    }
                }
            }
        }
    }
}

query IntradayHistorySummary {
    listings (
        scheme: TICKER_BC,
        ids: ["ABBN_4"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            listingShortName
            marketShortName
            listingCurrency
            listingStatus
        }
        marketData {
            intradayHistory {
                summary (
                    filters: {
                        dateFrom: "2023-10-22",
                        timeFrom: "07:45:15",
                        dateTo: "2023-10-29",
                        timeTo: "18:00:00",
                        period: "PT5M"
                    }
                ) {
                    intervalFrom
                    intervalTo
                    numberOfTrades
                    open
                    high
                    low
                    last
                    volume
                }
            }
        }
    }
}`};

const intradaySnapshotGraphQLQueryExample = {
    displayName: APIDisplayNames.IntradaySnapshot,
    query:
`query IntradaySnapshot {
    listings (
        ids: "AAPL_67",
        scheme: TICKER_BC
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            listingShortName
            marketShortName
            listingCurrency
            listingStatus
        }
        marketData {
            intradaySnapshot (
                qualityOfService: DELAYED
            ) {
                qualityOfService
                last {
                    value
                    size
                    timestamp
                }
                bestBid {
                    value
                    size
                    timestamp
                }
                bestAsk {
                    value
                    size
                    timestamp
                }
                spreadBestBidAsk {
                    value
                    timestamp
                }
                percentageSpreadBestBidAsk {
                    value
                    timestamp
                }
                open {
                    value
                    timestamp
                }
                high {
                    value
                    timestamp
                }
                low {
                    value
                    timestamp
                }
                close {
                    value
                    timestamp
                }
                highStaticLimit {
                    value
                    timestamp
                }
                lowStaticLimit {
                    value
                    timestamp
                }
                mid {
                    value
                    timestamp
                }
                numberOfTrades {
                    value
                    timestamp
                }
                openInterest {
                    value
                    timestamp
                }
                settlementPrice {
                    value
                    timestamp
                }
                yieldToMaturity {
                    value
                    timestamp
                }
                dividendYield {
                    value
                    timestamp
                }
                marketCapitalisation {
                    value
                    timestamp
                }
                volume {
                    value
                    timestamp
                }
                volumeTotal {
                    value
                    timestamp
                }
                turnover {
                    value
                    timestamp
                }
                turnoverTotal {
                    value
                    timestamp
                }
                vwap {
                    value
                    timestamp
                }
                priceEarningsRatioReported {
                    value
                    timestamp
                }
                priceEarningsRatioEstimated {
                    value
                    timestamp
                }
                delta {
                    value
                    timestamp
                }
                vega {
                    value
                    timestamp
                }
                gamma {
                    value
                    timestamp
                }
                theta {
                    value
                    timestamp
                }
                rho {
                    value
                    timestamp
                }
                omega {
                    value
                    timestamp
                }
                gearing {
                    value
                    timestamp
                }
                moneyness {
                    value
                    timestamp
                }
                breakEvenPoint {
                    value
                    timestamp
                }
                intrinsicValue {
                    value
                    timestamp
                }
                auctionTheoreticalMatchingPrice {
                    value
                    timestamp
                }
                impliedVolatility {
                    value
                    timestamp
                }
                historicalVolatility30Days {
                    value
                    timestamp
                }
                historicalVolatility90Days {
                    value
                    timestamp
                }
                historicalVolatility180Days {
                    value
                    timestamp
                }
                historicalVolatility250Days {
                    value
                    timestamp
                }
                closeYear {
                    value
                    timestamp
                }
                duration {
                    value
                    timestamp
                }
                modifiedDuration {
                    value
                    timestamp
                }
                convexity {
                    value
                    timestamp
                }
                accruedInterest {
                    value
                    timestamp
                }
                timeToMaturity {
                    value
                    timestamp
                }
                yieldToCall {
                    value
                    timestamp
                }
                totalExpenseRatio {
                    value
                    timestamp
                }
                percentageChangeYearToDate {
                    value
                    timestamp
                }
                high1Week {
                    value
                    timestamp
                }
                high4Week {
                    value
                    timestamp
                }
                high12Week {
                    value
                    timestamp
                }
                high26Week {
                    value
                    timestamp
                }
                high52Week {
                    value
                    timestamp
                }
                low1Week {
                    value
                    timestamp
                }
                low4Week {
                    value
                    timestamp
                }
                low12Week {
                    value
                    timestamp
                }
                low26Week {
                    value
                    timestamp
                }
                low52Week {
                    value
                    timestamp
                }
                highYearToDate {
                    value
                    timestamp
                }
                lowYearToDate {
                    value
                    timestamp
                }
            }
        }
    }
}`};

const entityBaseGraphQLQueryExample = {
    displayName: APIDisplayNames.EntityBase,
    query:
`query EntityBaseByInstrument {
    instruments (
        scheme: ISIN,
        ids: ["CH0009980894", "IE00B5BMR087", "CH0559601544", "US0378331005", "JE00B1VS3770", "US037833AK68", "XXX"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            instrumentShortName
            instrumentStatus
            instrumentType
        }
        referenceData {
            entityBase {
                entityShortName
                entityLongName
                gk
                lei
                entityType
                entityStatus
                language
                entityLocation
                entityCountry
            }
        }
    }
}

query EntityBaseByEntity {
    entities (
        scheme: LEI,
        ids: ["HWUPKR0MPOU8FGXBT394", "XXXXX"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            entityShortName
            entityType
            entityStatus
        }
        referenceData {
            entityBase {
                entityShortName
                entityLongName
                gk
                lei
                entityType
                entityStatus
                language
                entityLocation
                entityCountry
            }
        }
    }
}

query EntityBaseByListing {
    listings (
        scheme: VALOR_BC,
        ids: ["1222171_4", "908440_67"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            listingShortName
            marketShortName
            listingCurrency
            listingStatus
        }
        referenceData {
            entityBase {
                entityShortName
                entityLongName
                gk
                lei
                entityType
                entityStatus
                language
                entityLocation
                entityCountry
            }
        }
    }
}`};

const listingBaseGraphQLQueryExample = {
    displayName: APIDisplayNames.ListingBase,
    query:
`query ListingBase {
    listings (
        scheme: VALOR_BC,
        ids: ["1222171_4", "908440_67"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            listingShortName
            marketShortName
            listingCurrency
            listingStatus
        }
        referenceData {
            listingBase {
                instrument {
                    shortName
                    valor
                    isin
                }
                market {
                    shortName
                    longName
                    bc
                    mic
                }
                listingShortName
                listingCurrency
                listingStatus
                tradingStatus
                listingCategory
                ticker
                segmentMic
                priceQuoteType
                priceRepresentationType
                priceUnitSize
                priceUnitType
                volumeUnitSize
                volumeUnitType
                dayCountBasis
                dayCountBasisLabel
                tradingUnitType
                tradingNominalSize
            }
        }
    }
}`};

const instrumentBaseGraphQLQueryExample = {
    displayName: APIDisplayNames.InstrumentBase,
    query:
`query InstrumentBaseByInstrument {
    instruments (
        scheme: ISIN,
        ids: ["CH0009980894", "IE00B5BMR087", "CH0559601544", "US0378331005", "JE00B1VS3770", "US037833AK68", "XXX"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            instrumentShortName
            instrumentStatus
            instrumentType
        }
        referenceData {
            instrumentBase {
                instrumentShortName
                instrumentNamePrefix
                instrumentNameSuffix
                instrumentProductName
                valor
                isin
                instrumentType
                securityType
                instrumentStatus
                language
                issuer {
                    shortName
                    longName
                    lei
                    gk
                }
                mostLiquidMarket {
                    shortName
                    longName
                    bc
                    mic
                }
                nominalAmount
                nominalCurrency
                nominalPaidUp
                instrumentUnitType
                instrumentUnitSize
                currentCouponRate
                currentCouponType
                maturityDate
                maturityType
                baseCurrency
                tradingCurrency
                baseCryptoSymbol
                tradingCryptoSymbol
                contractType
                contractSymbol
                underlyingInstrument {
                    shortName
                    valor
                    isin
                }
                ultimateUnderlyingInstrument {
                    shortName
                    valor
                    isin
                }
                optionType
                optionStrikePrice
                optionStrikePriceCurrency
                exerciseType
                expirationDate
                contractSize
                contractUnitType
                contractMultiplier
                sixVersionNumber
            }
        }
    }
}

query InstrumentBaseByListing {
    listings (
        scheme: VALOR_BC,
        ids: ["1222171_4", "908440_67"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            listingShortName
            marketShortName
            listingCurrency
            listingStatus
        }
        referenceData {
            instrumentBase {
                instrumentShortName
                instrumentNamePrefix
                instrumentNameSuffix
                instrumentProductName
                valor
                isin
                instrumentType
                securityType
                instrumentStatus
                language
                issuer {
                    shortName
                    longName
                    lei
                    gk
                }
                mostLiquidMarket {
                    shortName
                    longName
                    bc
                    mic
                }
                nominalAmount
                nominalCurrency
                nominalPaidUp
                instrumentUnitType
                instrumentUnitSize
                currentCouponRate
                currentCouponType
                maturityDate
                maturityType
                baseCurrency
                tradingCurrency
                baseCryptoSymbol
                tradingCryptoSymbol
                contractType
                contractSymbol
                underlyingInstrument {
                    shortName
                    valor
                    isin
                }
                ultimateUnderlyingInstrument {
                    shortName
                    valor
                    isin
                }
                optionType
                optionStrikePrice
                optionStrikePriceCurrency
                exerciseType
                expirationDate
                contractSize
                contractUnitType
                contractMultiplier
                sixVersionNumber
            }
        }
    }
}`};

const instrumentSymbologyGraphQLQueryExample = {
    displayName: APIDisplayNames.InstrumentSymbology,
    query:
`query InstrumentSymbologyByInstrument {
    instruments (
        scheme: ISIN,
        ids: ["US0378331005", "IT0000062072", "XXXXX"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        referenceData {
            instrumentSymbology {
                valor
                isin
                wkn
                cusip
                cins
                sedol
                commonCode
                figiShareClass
                figi
                japaneseIssueCode
                japaneseNewSecuritiesCode
                cryptocurrencySymbol
            }
        }
    }
}

query InstrumentSymbologyByListing {
    listings (
        scheme: VALOR_BC,
        ids: ["1222171_4", "908440_67"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            listingShortName
            marketShortName
            listingCurrency
            listingStatus
        }
        referenceData {
            instrumentSymbology {
                valor
                isin
                wkn
                cusip
                cins
                sedol
                commonCode
                figiShareClass
                figi
                japaneseIssueCode
                japaneseNewSecuritiesCode
            }
        }
    }
}`};

const listingSymbologyGraphQLQueryExample = {
    displayName: APIDisplayNames.ListingSymbology,
    query:
`query ListingSymbology {
    listings (
        scheme: VALOR_BC,
        ids: ["1222171_4", "908440_67"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            listingShortName
            marketShortName
            listingCurrency
            listingStatus
        }
        referenceData {
            listingSymbology {
                ticker
                figi
                figiComposite
                sedol
                listingSymbol
                sixSymbol
                alternativeInstrumentIdentifier
            }
        }
    }
}`};

const marketBaseGraphQLQueryExample = {
    displayName: APIDisplayNames.MarketBase,
    query:
`query MarketBaseByMarket {
    markets (
        scheme: BC,
        ids: ["4", "67", "176", "257", "3553", "63008"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            marketShortName
            marketStatus
            marketType
        }
        referenceData {
            marketBase {
                marketLongName
                marketShortName
                language
                bc
                mic
                micType
                marketType
                marketStatus
                marketSymbol
                marketCountry
            }
        }
    }
}

query MarketBaseByListing {
    listings (
        scheme: TICKER_BC,
        ids: ["ABBN_4","AAPL_67","SMI_4","PFE_65","CHF/EUR_148","DRH_65"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            listingStatus
            listingCurrency
            listingShortName
            marketShortName
        }
        referenceData {
            marketBase {
                marketLongName
                marketShortName
                language
                bc
                mic
                micType
                marketType
                marketStatus
                marketSymbol
                marketCountry
            }
        }
    }
}`};

const instrumentMarketsGraphQLQueryExample = {
    displayName: APIDisplayNames.InstrumentMarkets,
    query:
`query InstrumentMarkets {
    instruments (
        scheme: ISIN,
        ids: ["CH0012005267"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            instrumentShortName
            instrumentStatus
            instrumentType
        }
        referenceData {
            instrumentMarkets (
                marketCountry: "CH",
                marketCriteria: [OFFICIAL_PLACE_OF_LISTING],
                listingCurrency: "CHF"
            ) {
                listingShortName
                listingCurrency
                listingStatus
                marketLongName
                marketShortName
                marketCountry
                bc
                mic
                ticker
                marketCriteria
            }
        }
    }
}`};

const freeTextSearchGraphQLQueryExample = {
    displayName: APIDisplayNames.FreeTextSearch,
    query:
`query FreeTextInstrumentsSearch {
    search {
        freeTextSearch (
            text: "Apple"
        ) {
            requestedText
            instruments {
                highlights {
                    matchingDescription
                }
                normalizedScore
                hit {
                    instrumentShortName
                    instrumentNamePrefix
                    instrumentNameSuffix
                    instrumentProductName
                    valor
                    isin
                    securityType
                    instrumentStatus
                    language
                    issuer {
                        shortName
                        longName
                        lei
                        gk
                    }
                    mostLiquidMarket {
                        shortName
                        longName
                        bc
                        mic
                    }
                    nominalAmount
                    nominalCurrency
                    nominalPaidUp
                    instrumentUnitType
                    instrumentUnitSize
                    currentCouponRate
                    currentCouponType
                    maturityDate
                    maturityType
                    baseCurrency
                    tradingCurrency
                    baseCryptoSymbol
                    tradingCryptoSymbol
                    contractType
                    contractSymbol
                    underlyingInstrument {
                        shortName
                        valor
                        isin
                    }
                    ultimateUnderlyingInstrument {
                        shortName
                        valor
                        isin
                    }
                    optionType
                    optionStrikePrice
                    optionStrikePriceCurrency
                    exerciseType
                    expirationDate
                    contractSize
                    contractUnitType
                    contractMultiplier
                    sixVersionNumber
                }
                normalizedScore
            }
        }
    }
}
   
query FreeTextEntitiesSearch {
    search {
        freeTextSearch (
            text: "Nestle"
        ) {
            requestedText
            entities {
                normalizedScore
                highlights {
                    matchingDescription
                }
                hit {
                    entityShortName
                    entityLongName
                    gk
                    lei
                    entityType
                    entityStatus
                    language
                    entityLocation
                    entityCountry
                }
            }
        }
    }
}
   
query FreeTextMarketsSearch {
    search {
        freeTextSearch (
            text: "SIX"
        ) {
            requestedText
            markets {
                hit {
                    marketLongName
                    marketShortName
                    language
                    bc
                    mic
                    micType
                    marketType
                    marketStatus
                    marketSymbol
                    marketCountry
                }
                normalizedScore
                highlights {
                    matchingDescription
                }
            }
        }
    }
}`};

const streamingIntradayTradesGraphQLQueryExample = {
    displayName: APIDisplayNames.StreamingIntradayTradesWithHistory,
    query:
`subscription intradayStream {
    startIntradayStream (
        scheme: TICKER_BC,
        ids: "AAPL_67",
        intradayHistory: true
    ) {
        type
        requestedId
        requestedScheme
        historyComplete
        lookup {
            listingStatus
            listingCurrency
            listingShortName
        }
        trade {
            value
            size
            timestamp
            unixTimestamp
        }
    }
}`};

const crireriaSearchGraphQLQueryExample = {
    displayName: APIDisplayNames.CriteriaSearch,
    query:
`query InstrumentCriteriaSearch {
    search {
        criteriaSearch {
            instruments (
                criteria: {
                    instrumentType: EQUITY
                    instrumentStatus: ACTIVE
                    openingDate: {
                        from: "2025-01-01"
                    }
                    market: {
                        scheme: BC
                        ids: "4"
                    }
                }
                size: 300,
                pageId: null
            ) {
                paging {
                    size
                    nextPageId
                }
                hits {
                    valor
                    isin
                    instrumentShortName
                }
            }
        }
    }
}

query ListingCriteriaSearch {
    search {
        criteriaSearch {
            listings (
                criteria: {
                    market: {
                        ids: ["148"],
                        scheme: BC
                    }
                }
                size: 300,  
                pageId: null
            ) {
                paging {
                    nextPageId
                    size
                }
                hits {
                    valor
                    listingShortName
                    listingCurrency
                }
            }
        }
    }
}`};

const estimatesGraphQLQueryExample = {
    displayName: APIDisplayNames.Estimates,
    query:
`query EstimatesByListing {
    listings (
        scheme: TICKER_BC,
        ids: ["AAPL_67"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            listingShortName
            marketShortName
            listingCurrency
            listingStatus
        }
        estimatesAndFundamentals {
            estimates {
                analystRecommendations {
                    hold
                    sell
                    buy
                    mark
                    total
                    outperform
                    underperform
                }
                fiscalYears (filters: {yearFrom: "2020" yearTo: "2023"}) {
                    year
                    yearEndDate
                    priceEarningsMedian
                    earningsPerShareMedian
                    earningsPerShareHigh
                    earningsPerShareLow
                    earningsPerShareOneMonthRevisions
                    earningsPerShareThreeMonthRevisions
                    earningsPerShareSixMonthRevisions
                    earningsPerShareRevisionsDown
                    earningsPerShareRevisionsUp
                    earningsPerShareRevisionsUnchanged
                    priceBookValuePerShareMedian
                    bookValuePerShareMedian
                    dividendYield
                    dividendMedian
                    priceCashFlowPerShareMedian
                    cashFlowPerShareMedian
                    salesMedian
                    netIncomeMedian
                    ebitMedian
                    ebitdaMedian
                    netDebtMedian
                }
                targetPriceMedian
                longTermGrowth
                earningsPerShareNextTwelveMonths
            }
        }
    }
}

query EstimatesByInstrument {
    instruments (
        scheme: VALOR,
        ids: ["908440"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            instrumentType
            instrumentStatus
            instrumentShortName
        }
        estimatesAndFundamentals {
            estimates {
                analystRecommendations {
                    hold
                    sell
                    buy
                    mark
                    total
                    outperform
                    underperform
                }
                fiscalYears (filters: {yearFrom: "2020" yearTo: "2023"}) {
                    year
                    yearEndDate
                    priceEarningsMedian
                    earningsPerShareMedian
                    earningsPerShareHigh
                    earningsPerShareLow
                    earningsPerShareOneMonthRevisions
                    earningsPerShareThreeMonthRevisions
                    earningsPerShareSixMonthRevisions
                    earningsPerShareRevisionsDown
                    earningsPerShareRevisionsUp
                    earningsPerShareRevisionsUnchanged
                    priceBookValuePerShareMedian
                    bookValuePerShareMedian
                    dividendYield
                    dividendMedian
                    priceCashFlowPerShareMedian
                    cashFlowPerShareMedian
                    salesMedian
                    netIncomeMedian
                    ebitMedian
                    ebitdaMedian
                    netDebtMedian
                }
                targetPriceMedian
                longTermGrowth
                earningsPerShareNextTwelveMonths
            }
        }
    }
}`}

const fundamentalsGraphQLQueryExample = {
    displayName: APIDisplayNames.Fundamentals,
    query:
`query FundamentalsByListing {
    listings (
        scheme: TICKER_BC,
        ids: ["AAPL_67"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            listingShortName
            marketShortName
            listingCurrency
            listingStatus
        }
        estimatesAndFundamentals {
            fundamentals {
                businessDescription
                ownership {
                    floatingShares
                    percentageFloatingShares
                    nonFloatingShares
                    totalSharesHeld
                    summary {
                        shareholderType
                        sharesHeld
                        percentageSharesHeld
                        marketValue
                    }
                    topShareholders {
                        currentRank
                        name
                        currentSharesHeld
                        currentPercentageSharesHeld
                        currentMarketValue
                        previousQuarterPercentageSharesHeld
                        prePreviousQuarterPercentageSharesHeld
                    }
                }
                fiscalYears (filters: {yearFrom: "2023" yearTo:"2023"}) {
                    year
                    yearEndDate
                    bookValuePerShare
                    cashFlowPerShare
                    dividendPerShare
                    dividendPayoutPerShare
                    earningsPerShare
                    marketCapitalisation
                    cashAndCashEquivalents
                    cashAndShortTermInvestments
                    totalAccountsReceivable
                    inventories
                    currentAssets
                    netPropertyPlantAndEquipment
                    goodwillAndIntangibles
                    totalOtherLongTermAssets
                    totalAssets
                    preferredEquity
                    totalEquity
                    currentLiabilities
                    longTermDebt
                    totalLiabilities
                    totalMinorityInterest
                    totalLiabilitiesAndEquity
                    revenue
                    ebitda
                    ebit
                    incomeTaxExpense
                    otherMinorityInterestExceptionals
                    netIncome
                    goodwillImpairment
                    netIncomeAvailableToCommonShareholders
                    netDebt
                    operatingNetCashFlow
                    capitalExpenditure
                    investingNetCashFlow
                    financingNetCashFlow
                    netChangeCash
                    leveragedFreeCashFlow
                    totalEmployees
                    returnOnAssets
                    returnOnCapital
                    returnOnEquity
                    grossMargin
                    ebitdaMargin
                    ebitMargin
                    incomeFromContinuingOperationsMargin
                    netIncomeMargin
                    currentRatio
                    quickRatio
                    averageDaysInventoryOutstanding
                    averageDaysSalesOutstanding
                    averageDaysPayableOutstanding
                    averageCashConversionCycle
                    totalAssetTurnover
                    fixedAssetTurnover
                    accountsReceivableTurnover
                    inventoryTurnover
                    totalDebtToEquity
                    totalDebtToCapital
                    longTermDebtToCapital
                    totalLiabilitiesToTotalAssets
                    ebitdaToInterestExpenses
                    ebitdaToInterestExpenses
                    ebitdaMinusCapexToInterestExpenses
                    totalDebtToEbitda
                    netDebtToEbitda      
                }
                fiscalQuarters (filters: { yearFrom:"2023" yearTo:"2023" }) {
                    year
                    quarterEndDate
                    bookValuePerShare
                    cashFlowPerShare
                    earningsPerShare
                    marketCapitalisation
                    cashAndCashEquivalents
                    cashAndShortTermInvestments
                    totalAccountsReceivable
                    inventories
                    currentAssets
                    netPropertyPlantAndEquipment
                    goodwillAndIntangibles
                    totalOtherLongTermAssets
                    totalAssets
                    preferredEquity
                    totalEquity
                    currentLiabilities
                    longTermDebt
                    totalLiabilities
                    totalMinorityInterest
                    totalLiabilitiesAndEquity
                    revenue
                    ebitda
                    ebit
                    incomeTaxExpense
                    otherMinorityInterestExceptionals
                    netIncome
                    goodwillImpairment
                    netIncomeAvailableToCommonShareholders
                    netDebt
                    operatingNetCashFlow
                    capitalExpenditure
                    investingNetCashFlow
                    financingNetCashFlow
                    netChangeCash
                    leveragedFreeCashFlow
                }
            }
        }
    }
}

query FundamentalsByInstrument {
    instruments (
        scheme: VALOR,
        ids: ["908440"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            instrumentType
            instrumentStatus
            instrumentShortName
        }
        estimatesAndFundamentals {
            fundamentals {
                businessDescription
                ownership {
                    floatingShares
                    percentageFloatingShares
                    nonFloatingShares
                    totalSharesHeld
                    summary {
                        shareholderType
                        sharesHeld
                        percentageSharesHeld
                        marketValue
                    }
                    topShareholders {
                        currentRank
                        name
                        currentSharesHeld
                        currentPercentageSharesHeld
                        currentMarketValue
                        previousQuarterPercentageSharesHeld
                        prePreviousQuarterPercentageSharesHeld
                    }
                }
                fiscalYears (filters: {yearFrom: "2023" yearTo:"2023"}) {
                    year
                    yearEndDate
                    bookValuePerShare
                    cashFlowPerShare
                    dividendPerShare
                    dividendPayoutPerShare
                    earningsPerShare
                    marketCapitalisation
                    cashAndCashEquivalents
                    cashAndShortTermInvestments
                    totalAccountsReceivable
                    inventories
                    currentAssets
                    netPropertyPlantAndEquipment
                    goodwillAndIntangibles
                    totalOtherLongTermAssets
                    totalAssets
                    preferredEquity
                    totalEquity
                    currentLiabilities
                    longTermDebt
                    totalLiabilities
                    totalMinorityInterest
                    totalLiabilitiesAndEquity
                    revenue
                    ebitda
                    ebit
                    incomeTaxExpense
                    otherMinorityInterestExceptionals
                    netIncome
                    goodwillImpairment
                    netIncomeAvailableToCommonShareholders
                    netDebt
                    operatingNetCashFlow
                    capitalExpenditure
                    investingNetCashFlow
                    financingNetCashFlow
                    netChangeCash
                    leveragedFreeCashFlow
                    totalEmployees
                    returnOnAssets
                    returnOnCapital
                    returnOnEquity
                    grossMargin
                    ebitdaMargin
                    ebitMargin
                    incomeFromContinuingOperationsMargin
                    netIncomeMargin
                    currentRatio
                    quickRatio
                    averageDaysInventoryOutstanding
                    averageDaysSalesOutstanding
                    averageDaysPayableOutstanding
                    averageCashConversionCycle
                    totalAssetTurnover
                    fixedAssetTurnover
                    accountsReceivableTurnover
                    inventoryTurnover
                    totalDebtToEquity
                    totalDebtToCapital
                    longTermDebtToCapital
                    totalLiabilitiesToTotalAssets
                    ebitdaToInterestExpenses
                    ebitdaToInterestExpenses
                    ebitdaMinusCapexToInterestExpenses
                    totalDebtToEbitda
                    netDebtToEbitda      
                }
                fiscalQuarters (filters: { yearFrom:"2023" yearTo:"2023" }) {
                    year
                    quarterEndDate
                    bookValuePerShare
                    cashFlowPerShare
                    earningsPerShare
                    marketCapitalisation
                    cashAndCashEquivalents
                    cashAndShortTermInvestments
                    totalAccountsReceivable
                    inventories
                    currentAssets
                    netPropertyPlantAndEquipment
                    goodwillAndIntangibles
                    totalOtherLongTermAssets
                    totalAssets
                    preferredEquity
                    totalEquity
                    currentLiabilities
                    longTermDebt
                    totalLiabilities
                    totalMinorityInterest
                    totalLiabilitiesAndEquity
                    revenue
                    ebitda
                    ebit
                    incomeTaxExpense
                    otherMinorityInterestExceptionals
                    netIncome
                    goodwillImpairment
                    netIncomeAvailableToCommonShareholders
                    netDebt
                    operatingNetCashFlow
                    capitalExpenditure
                    investingNetCashFlow
                    financingNetCashFlow
                    netChangeCash
                    leveragedFreeCashFlow
                }
            }
        }
    }
}`}

const endOfDaySnapshotGraphQLQueryExample = {
    displayName: APIDisplayNames.EndOfDaySnapshot,
    query:
`query EndOfDaySnapshot {
    listings (
        ids: "AAPL_67",
        scheme: TICKER_BC
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            listingShortName
            marketShortName
            listingCurrency
            listingStatus
        }
        marketData {
            endOfDaySnapshot {
                open {
                    value
                    size
                    timestamp
                }
                high {
                    value
                    size
                    timestamp
                }
                low {
                    value
                    size
                    timestamp
                }
                close {
                    value
                    size
                    timestamp
                }
                mid {
                    value
                    timestamp
                }
                closingBid {
                    value
                    size
                    timestamp
                }
                highStaticLimit {
                    value
                    timestamp
                }
                lowStaticLimit {
                    value
                    timestamp
                }
                numberOfTrades {
                    value
                    timestamp
                }
                auctionTheoreticalMatchingPrice {
                    value
                    timestamp
                }
                openInterest {
                    value
                    timestamp
                }
                settlementPrice {
                    value
                    timestamp
                }
                yieldToMaturity {
                    value
                    timestamp
                }
                dividendYield {
                    value
                    timestamp
                }
                marketCapitalisation {
                    value
                    timestamp
                }
                volume {
                    value
                    timestamp
                }
                volumeTotal {
                    value
                    timestamp
                }
                turnover {
                    value
                    timestamp
                }
                turnoverTotal {
                    value
                    timestamp
                }
                priceEarningsRatioReported {
                    value
                    timestamp
                }
                priceEarningsRatioEstimated {
                    value
                    timestamp
                }
                delta {
                    value
                    timestamp
                }
                vega {
                    value
                    timestamp
                }
                gamma {
                    value
                    timestamp
                }
                theta {
                    value
                    timestamp
                }
                rho {
                    value
                    timestamp
                }
                omega {
                    value
                    timestamp
                }
                gearing {
                    value
                    timestamp
                }
                moneyness {
                    value
                    timestamp
                }
                breakEvenPoint {
                    value
                    timestamp
                }
                intrinsicValue {
                    value
                    timestamp
                }
                impliedVolatility {
                    value
                    timestamp
                }
                historicalVolatility30Days {
                    value
                    timestamp
                }
                historicalVolatility90Days {
                    value
                    timestamp
                }
                historicalVolatility180Days {
                    value
                    timestamp
                }
                historicalVolatility250Days {
                    value
                    timestamp
                }
                closeYear {
                    value
                    timestamp
                }
                latestDividend {
                    value
                    timestamp
                }
                annualisedDividend {
                    value
                    timestamp
                }
                indicatedAnnualDividend {
                    value
                    timestamp
                }
                averageDailyVolumeMonthToDate {
                    value
                    timestamp
                }
                averageDailyVolumePreviousMonth {
                    value
                    timestamp
                }
                duration {
                    value
                    timestamp
                }
                modifiedDuration {
                    value
                    timestamp
                }
                convexity {
                    value
                    timestamp
                }
                accruedInterest {
                    value
                    timestamp
                }
                timeToMaturity {
                    value
                    timestamp
                }
                yieldToCall {
                    value
                    timestamp
                }
                totalExpenseRatio {
                    value
                    timestamp
                }
                vwap {
                    value
                    timestamp
                }
            }
        }
    }
}`};

const indexCompositionGraphQLQueryExample = {
    displayName: APIDisplayNames.IndexComposition,
    query:
`query IndexComposition {
    instruments (
        ids: "CH1292887457",
        scheme: ISIN
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            instrumentShortName
            instrumentStatus
            instrumentType
        }
        referenceData {
            indexComposition {
                divisor
                numberOfConstituents
                constituents {
                    valor
                    isin
                    bc
                    mic
                    currency
                    freeFloatFactor
                    weight
                  	indexShares
                }
            }
        }
    }
}
query ListingIndexComposition {
    listings (
        ids: "CH1292887457_3970",
        scheme: ISIN_BC
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
          listingShortName
          listingCurrency
          listingStatus
          marketShortName
        }
        referenceData {
            indexComposition {
                divisor
                numberOfConstituents
                constituents {
                    valor
                    isin
                    bc
                    mic
                    currency
                    freeFloatFactor
                    weight
                  	indexShares
                }
            }
        }
    }
}`};

const entityClassificationGraphQLQueryExample = {
    displayName: APIDisplayNames.EntityClassification,
    query:
`query EntityClassificationByInstrument {
    instruments (
        scheme: ISIN,
        ids: [
            "CH0244767585",
            "SE0016274385",
            "FR0004153153",
            "IE00B5BMR087",
            "CH0559601544",
            "US0378331005",
            "JE00B1VS3770",
            "US037833AK68",
            "XXXX"
        ]
    ) {
        requestedId
        requestedScheme
        lookup {
            instrumentShortName
            instrumentType
            instrumentStatus
        }
        lookupStatus
        referenceData {
            entityClassification {
                sixSector
                gicsSubIndustryCode
                gicsSubIndustryName
                gicsSubIndustryDescription
                naceCode
                naceClassName
                icbSubsectorCode
                icbSubsectorName
                sicCode
                sicName
                nafSubclassCode
                nafSubclassName
                fitchMarketSectorCode
                fitchMarketSectorName
                banqueDeFranceClassificationCode
                banqueDeFranceClassificationName
                banqueDeFranceClassificationDescription
                sixBanqueDeFranceClassificationCode
                sixBanqueDeFranceClassificationName
                sixBanqueDeFranceClassificationDescription
                ebaClassification
            }
        }
    }
}
   
query EntityClassificationByListing {
    listings (
        scheme: ISIN_BC,
        ids: [
            "CH0244767585_4",
            "US0378331005_67",
            "IE00B5BMR087_361",
            "XXXX"
        ]
    ) {
        requestedId
        requestedScheme
        lookup {
            listingShortName
            marketShortName
            listingCurrency
            listingStatus
        }
        lookupStatus
        referenceData {
            entityClassification {
                sixSector
                gicsSubIndustryCode
                gicsSubIndustryName
                gicsSubIndustryDescription
                naceCode
                naceClassName
                icbSubsectorCode
                icbSubsectorName
                sicCode
                sicName
                nafSubclassCode
                nafSubclassName
                fitchMarketSectorCode
                fitchMarketSectorName
                banqueDeFranceClassificationCode
                banqueDeFranceClassificationName
                banqueDeFranceClassificationDescription
                sixBanqueDeFranceClassificationCode
                sixBanqueDeFranceClassificationName
                sixBanqueDeFranceClassificationDescription
                ebaClassification
            }
        }
    }
}`};

const instrumentClassificationGraphQLQueryExample = {
    displayName: APIDisplayNames.InstrumentClassification,
    query:
`query instrumentClassificationByInstrument {
    instruments (
        scheme: ISIN, 
        ids: [
            "CH0009980894",
            "IE00B5BMR087",
            "CH0559601544",
            "US0378331005",
            "JE00B1VS3770",
            "US037833AK68",
            "XXX"
        ], 
        preferredLanguage: EN
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            instrumentShortName
            instrumentStatus
            instrumentType
        }
        referenceData {
            instrumentClassification {
                instrumentTypeSupplement
                cfi
                sixCfi
                connexorIbtTypeCode
                connexorIbtTypeDescription
                eusipaClassificationCode
                eusipaClassificationDescription
                sspaClassificationCode
                sspaClassificationDescription
                euronextStructuredProductsType
                etdContractSpecificationType
                collectiveInvestmentVehicleSubType
                crdClassification
                europeanCentralBankClassification
                marketAffiliation
                borrowerCategory
                creditRiskMeasure
                marketRiskMeasure
                productRiskIndicator
                sspaValueAtRiskFigure
                euInterestIncomeTaxCategory
                tokenClassification
                referenceCryptoAssetClassification
                currencyClassification
                usAgencyFreddieMacPrefix
                usAgencyFreddieMacPrefixDescription
                usAgencyFannieMaePrefix
                usAgencyFannieMaePrefixDescription
                usAgencyGinnieMaePrefix
                usAgencyGinnieMaePrefixDescription
                germanMarketDebtClassification
            }
        }
    }
}

query instrumentClassificationByListing {
    listings(
        scheme: VALOR_BC, 
        ids: [
            "1222171_4",
            "908440_67"
        ], 
        preferredLanguage: EN
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            listingShortName
            marketShortName
            listingCurrency
            listingStatus
        }
        referenceData {
            instrumentClassification {
                instrumentTypeSupplement
                cfi
                sixCfi
                connexorIbtTypeCode
                connexorIbtTypeDescription
                eusipaClassificationCode
                eusipaClassificationDescription
                sspaClassificationCode
                sspaClassificationDescription
                euronextStructuredProductsType
                etdContractSpecificationType
                collectiveInvestmentVehicleSubType
                crdClassification
                europeanCentralBankClassification
                marketAffiliation
                borrowerCategory
                creditRiskMeasure
                marketRiskMeasure
                productRiskIndicator
                sspaValueAtRiskFigure
                euInterestIncomeTaxCategory
                tokenClassification
                referenceCryptoAssetClassification
                currencyClassification
                usAgencyFreddieMacPrefix
                usAgencyFreddieMacPrefixDescription
                usAgencyFannieMaePrefix
                usAgencyFannieMaePrefixDescription
                usAgencyGinnieMaePrefix
                usAgencyGinnieMaePrefixDescription
                germanMarketDebtClassification
            }
        }
    }
}`};

const swissStampDutyGraphQLQueryExample = {
    displayName: APIDisplayNames.SwissStampDuty,
    query:
`query SwissStampDutyInstrument {
    instruments (
        ids: "56520470",
        scheme: VALOR
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            instrumentShortName
            instrumentStatus
            instrumentType
        }
        tax {
            swissStampDuty {
                taxableSecurity
                taxApplicability
                issuerCountry 
            }
        }
    }
}

query SwissStampDutyListing {
    listings (
        ids: "56520470_857",
        scheme: VALOR_BC
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            listingShortName
            marketShortName
            listingCurrency
            listingStatus
        }
        tax {
            swissStampDuty {
                taxableSecurity
                taxApplicability
                issuerCountry 
            }
        }
    }
}`};

const instrumentDetailGraphQLQueryExample = {
    displayName: APIDisplayNames.InstrumentDetail,
    query:
`query InstrumentDetailByInstrument {
    instruments (
        scheme: VALOR
        ids: ["1222171","975519","908440"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            instrumentShortName
            instrumentStatus
            instrumentType
        }
        referenceData {
            instrumentDetail {
                openingDate
                deletionDate
                deletionReason
                fisn
                sixFisn
                fisnSecuritiesAbbreviation
                fisnAllocationType
                instrumentProductBrand
                redenominationMethod
                originalNominalAmount
                originalNominalCurrency
                instrumentLegalForm
                monitoringRestriction
                mainUnderlyingAssetClass
                secondLevelMainUnderlyingAssetClass
                callable
                puttable
                amortization
                partlyPaidIssue
                extensionByIssuer
                extensionByHolder
                conversionByHolder
                conversionByIssuer
                repaymentsInOtherCurrencies
                reopeningClause
                coveredWarrant
                interestDueEndOfMonth
                withoutPhysicalCoupon
                callableForTaxReasons
                notionalAmount
                countryOfApplicableLaw
                currencyRiskHedged
                autocallPossible
                collateralSecuredInstrument
                distributionsInOtherCurrencies
                nominalReduction
                seniority
                voteCarrying
                certificate
                certificateType
                icmaRegulated
                combinedInstrument
                rightType
                exchangeable
                inflationProtected
                insuranceLinked
                overallConversionOrExercisePeriodDateFrom
                overallConversionOrExercisePeriodDateTo
                nextCashDividend {
                    exDividendDate
                    paymentDate
                    paymentStatus
                    grossAmount
                    grossAmountCurrency
                    dividendType
                    dividendPaymentFrequency
                }
                previousCashDividend {
                    exDividendDate
                    paymentDate
                    paymentStatus
                    grossAmount
                    grossAmountCurrency
                    dividendType
                    dividendPaymentFrequency
                }
                newInstruments {
                    shortName
                    valor
                    isin
                }
                outstandingCapital
                outstandingNumberOfInstruments
                outstandingCapitalEffectiveDate
                issuedCapital
                issuedNumberOfInstruments
                issuedCapitalEffectiveDate
                treasuryStock
                treasuryStockNumberOfInstruments
                treasuryStockEffectiveDate
                totalNumberOfVotingRights
                totalNumberOfVotingRightsEffectiveDate
                freeFloatNumberOfInstruments
                freeFloatEffectiveDate
                equityType
                preferredDividendType
                numberOfVotingRightsPerShare
                legalMaturityDate
                parValueType
                statedValue
                statedValueCurrency
                dividendReinvestmentPlan
                currentDividendPaymentFrequency
                earningsType
                earningsWholeTermType
                subjectToWithholdingTax
                capitalProtection
                fundType
                closedEndFund
                fundDistributionPolicy
                fundPerformanceCurrency
                commodityType
                commodityCategory
                commodityName
                commodityForm
                commodityNumberOfUnits
                commodityUnitType
                commodityQualityLevel
                commodityFeatures
                combinedTransactionType
                expirationTime
                acceleratedExpirationDate
                deliveryLastDate
                exchangeGenerationNumber
                exchangeVersionNumber
                contractSpecification {
                    shortName
                    valor
                    isin
                }
                underlyingReferenceMarket {
                    shortName
                    longName
                    bc
                    mic
                }
                contractualSettlementDate
                positionLimit
                contractDateYear
                contractDateMonth
                contractDateDay
                originalOptionStrikePrice
                strikePriceRepresentation
                contractSizeType
                contractQuantityDeliverable
                contractFrequency
                settlementType
                cashCompensationAmount
                cashCompensationCurrency
                standardContract
                etdSuccessorInstrument {
                    shortName
                    valor
                    transferredIsin
                    transferredIsinStatus
                    transferredIsinDateFrom
                    transferredIsinDateTo
                }
                datedDate
                firstCouponDate
                couponCapitalization
                delayedInterestPaymentInMonths
                delayedInterestPaymentDay
                dayCountBasis
                dayCountBasisLabel
                businessDayConvention
                couponPeriodAdjustment
                couponPaymentFrequency
                couponPaymentFrequencyTimeUnit
                irregularFirstCoupon
                irregularLastCoupon
                redemptionRatio
                redemptionAmount
                redemptionAmountCurrency
                nextCoupon {
                    significantDate
                    paymentDate
                    paymentStatus
                    grossRate
                    entitlementPeriodStartDate
                    entitlementPeriodEndDate
                }
                previousCoupon {
                    significantDate
                    paymentDate
                    paymentStatus
                    grossRate
                    entitlementPeriodStartDate
                    entitlementPeriodEndDate
                }
                debtAdjustmentFactors {
                    type
                    factor
                    dueDate
                    validUntilDate
                }
                nominalInflationProtected {
                    indexRatio
                    dueDate
                }
                instrumentDependencies {
                    shortName
                    valor
                    isin
                    dependencyType
                    numberOfDependentInstruments
                    numberOfIndependentInstruments
                }
            }
        }
    }
}
    
query InstrumentDetailByListing {
    listings (
        scheme: VALOR_BC
        ids: ["1222171_4","975519_4","908440_67"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            listingShortName
            marketShortName
            listingCurrency
            listingStatus
        }
        referenceData {
            instrumentDetail {
                openingDate
                deletionDate
                deletionReason
                fisn
                sixFisn
                fisnSecuritiesAbbreviation
                fisnAllocationType
                instrumentProductBrand
                redenominationMethod
                originalNominalAmount
                originalNominalCurrency
                instrumentLegalForm
                monitoringRestriction
                mainUnderlyingAssetClass
                secondLevelMainUnderlyingAssetClass
                callable
                puttable
                amortization
                partlyPaidIssue
                extensionByIssuer
                extensionByHolder
                conversionByHolder
                conversionByIssuer
                repaymentsInOtherCurrencies
                reopeningClause
                coveredWarrant
                interestDueEndOfMonth
                withoutPhysicalCoupon
                callableForTaxReasons
                notionalAmount
                countryOfApplicableLaw
                currencyRiskHedged
                autocallPossible
                collateralSecuredInstrument
                distributionsInOtherCurrencies
                nominalReduction
                seniority
                voteCarrying
                certificate
                certificateType
                icmaRegulated
                combinedInstrument
                rightType
                exchangeable
                inflationProtected
                insuranceLinked
                overallConversionOrExercisePeriodDateFrom
                overallConversionOrExercisePeriodDateTo
                nextCashDividend {
                    exDividendDate
                    paymentDate
                    paymentStatus
                    grossAmount
                    grossAmountCurrency
                    dividendType
                    dividendPaymentFrequency
                }
                previousCashDividend {
                    exDividendDate
                    paymentDate
                    paymentStatus
                    grossAmount
                    grossAmountCurrency
                    dividendType
                    dividendPaymentFrequency
                }
                newInstruments {
                    shortName
                    valor
                    isin
                }
                outstandingCapital
                outstandingNumberOfInstruments
                outstandingCapitalEffectiveDate
                issuedCapital
                issuedNumberOfInstruments
                issuedCapitalEffectiveDate
                treasuryStock
                treasuryStockNumberOfInstruments
                treasuryStockEffectiveDate
                totalNumberOfVotingRights
                totalNumberOfVotingRightsEffectiveDate
                freeFloatNumberOfInstruments
                freeFloatEffectiveDate
                equityType
                preferredDividendType
                numberOfVotingRightsPerShare
                legalMaturityDate
                parValueType
                statedValue
                statedValueCurrency
                dividendReinvestmentPlan
                currentDividendPaymentFrequency
                earningsType
                earningsWholeTermType
                subjectToWithholdingTax
                capitalProtection
                fundType
                closedEndFund
                fundDistributionPolicy
                fundPerformanceCurrency
                commodityType
                commodityCategory
                commodityName
                commodityForm
                commodityNumberOfUnits
                commodityUnitType
                commodityQualityLevel
                commodityFeatures
                combinedTransactionType
                expirationTime
                acceleratedExpirationDate
                deliveryLastDate
                exchangeGenerationNumber
                exchangeVersionNumber
                contractSpecification {
                    shortName
                    valor
                    isin
                }
                underlyingReferenceMarket {
                    shortName
                    longName
                    bc
                    mic
                }
                contractualSettlementDate
                positionLimit
                contractDateYear
                contractDateMonth
                contractDateDay
                originalOptionStrikePrice
                strikePriceRepresentation
                contractSizeType
                contractQuantityDeliverable
                contractFrequency
                settlementType
                cashCompensationAmount
                cashCompensationCurrency
                standardContract
                etdSuccessorInstrument {
                    shortName
                    valor
                    transferredIsin
                    transferredIsinStatus
                    transferredIsinDateFrom
                    transferredIsinDateTo
                }
                datedDate
                firstCouponDate
                couponCapitalization
                delayedInterestPaymentInMonths
                delayedInterestPaymentDay
                dayCountBasis
                dayCountBasisLabel
                businessDayConvention
                couponPeriodAdjustment
                couponPaymentFrequency
                couponPaymentFrequencyTimeUnit
                irregularFirstCoupon
                irregularLastCoupon
                redemptionRatio
                redemptionAmount
                redemptionAmountCurrency
                nextCoupon {
                    significantDate
                    paymentDate
                    paymentStatus
                    grossRate
                    entitlementPeriodStartDate
                    entitlementPeriodEndDate
                }
                previousCoupon {
                    significantDate
                    paymentDate
                    paymentStatus
                    grossRate
                    entitlementPeriodStartDate
                    entitlementPeriodEndDate
                }
                debtAdjustmentFactors {
                    type
                    factor
                    dueDate
                    validUntilDate
                }
                nominalInflationProtected {
                    indexRatio
                    dueDate
                }
                instrumentDependencies {
                    shortName
                    valor
                    isin
                    dependencyType
                    numberOfDependentInstruments
                    numberOfIndependentInstruments
                }
            }
        }
    }
}`};

const marketHolidaysGraphQLQueryExample = {
    displayName: APIDisplayNames.MarketHolidays,
    query:
`query MarketHolidaysByMarket {
    markets (
        scheme: BC,
        ids: ["4", "67"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            marketShortName
            marketStatus
            marketType
        }
        referenceData {
            marketHolidays (calendarYear: 2026) {
                date
                name
                extraordinaryTradingDay
                extraordinaryOpeningTime
                extraordinaryClosingTime
            }
        }
    }
}

query MarketHolidaysByListing {
    listings (
        scheme: TICKER_BC,
        ids: ["ABBN_4", "AAPL_67"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            listingStatus
            listingCurrency
            listingShortName
            marketShortName
        }
        referenceData {
            marketHolidays (calendarYear: 2026) {
                date
                name
                extraordinaryTradingDay
                extraordinaryOpeningTime
                extraordinaryClosingTime
            }
        }
    }
}`};


const mifidIISuitabilityGraphQLQueryExample = {
    displayName: APIDisplayNames.MifidIISuitability,
    query:
`query MiFIDIISuitabilityInstrument {
    instruments (
        ids: "56520470",
        scheme: VALOR
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            instrumentShortName
            instrumentStatus
            instrumentType
        }
        investorProtection {
            mifidIISuitability {
                regulationLocation
                indicators {
                    priipsScopeApplicability
                    bailInPowerApplicability
                    mifidIIEligibleInstrument {
                        applicability
                        validDateFrom
                        allocationType
                    }
                    complexity {
                        indicator
                        applicability
                        validDateFrom
                        validDateTo
                        allocationType
                    }
                    leveragedInstrument {
                        applicability
                        validDateFrom
                        allocationType
                    }
                    distributionOfCash {
                        applicability
                        validDateFrom
                        allocationType
                    }
                    performanceFee {
                        applicability
                        validDateFrom
                        allocationType
                    }
                    fundShareClassWithoutRetrocession {
                        applicability
                        validDateFrom
                        allocationType
                    }
                    exPostCostCalculationBasisItaly {
                        indicator
                        validDateFrom
                        allocationType
                    }
                    emtProductType {
                        productType
                        validDateFrom
                        allocationType
                    }
                    productGovernanceProcess {
                        indicator
                        validDateFrom
                        allocationType
                    }
                    germanProductCategory {
                        category
                        validDateFrom
                        allocationType
                    }
                    priipsSri {
                        amount
                        validDateFrom
                        allocationType
                    }
                    ucitsSrri {
                        amount
                        validDateFrom
                        allocationType
                    }
                }
                targetMarketSection {
                    investorTypes {
                        investorType
                        applicability
                        targetMarket
                        validDateFrom
                        allocationType
                    }
                    knowledgeAndExperience {
                        level
                        applicability
                        targetMarket
                        validDateFrom
                        allocationType
                    }
                    abilityToBearLosses {
                        capacity
                        applicability
                        targetMarket
                        validDateFrom
                        allocationType
                        limitedCapitalLossLevel
                    }
                    riskAndRewardProfiles {
                        profile
                        applicability
                        targetMarket
                        validDateFrom
                        allocationType
                    }
                    investmentObjectives {
                        objective
                        applicability
                        targetMarket
                        validDateFrom
                        allocationType
                        recommendedHoldingPeriod
                        recommendedHoldingPeriodTimeUnit
                        recommendedHoldingPeriodInYears
                        specificInvestmentNeedDetails {
                            need
                            applicability
                            targetMarket
                            validDateFrom
                            allocationType
                        }
                    }
                    distributionStrategies {
                        strategy
                        applicability
                        targetMarket
                        clientType
                        validDateFrom
                        allocationType
                    }
                }
            }
        }
    }
}
    
query MiFIDIISuitabilityListing {
    listings (
        ids: "AAPL_67",
        scheme: TICKER_BC
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            listingStatus
            listingCurrency
            listingShortName
            marketShortName
        }
        investorProtection {
            mifidIISuitability {
                regulationLocation
                indicators {
                    priipsScopeApplicability
                    bailInPowerApplicability
                    mifidIIEligibleInstrument {
                        applicability
                        validDateFrom
                        allocationType
                    }
                    complexity {
                        indicator
                        applicability
                        validDateFrom
                        validDateTo
                        allocationType
                    }
                    leveragedInstrument {
                        applicability
                        validDateFrom
                        allocationType
                    }
                    distributionOfCash {
                        applicability
                        validDateFrom
                        allocationType
                    }
                    performanceFee {
                        applicability
                        validDateFrom
                        allocationType
                    }
                    fundShareClassWithoutRetrocession {
                        applicability
                        validDateFrom
                        allocationType
                    }
                    exPostCostCalculationBasisItaly {
                        indicator
                        validDateFrom
                        allocationType
                    }
                    emtProductType {
                        productType
                        validDateFrom
                        allocationType
                    }
                    productGovernanceProcess {
                        indicator
                        validDateFrom
                        allocationType
                    }
                    germanProductCategory {
                        category
                        validDateFrom
                        allocationType
                    }
                    priipsSri {
                        amount
                        validDateFrom
                        allocationType
                    }
                    ucitsSrri {
                        amount
                        validDateFrom
                        allocationType
                    }
                }
                targetMarketSection {
                    investorTypes {
                        investorType
                        applicability
                        targetMarket
                        validDateFrom
                        allocationType
                    }
                    knowledgeAndExperience {
                        level
                        applicability
                        targetMarket
                        validDateFrom
                        allocationType
                    }
                    abilityToBearLosses {
                        capacity
                        applicability
                        targetMarket
                        validDateFrom
                        allocationType
                        limitedCapitalLossLevel
                    }
                    riskAndRewardProfiles {
                        profile
                        applicability
                        targetMarket
                        validDateFrom
                        allocationType
                    }
                    investmentObjectives {
                        objective
                        applicability
                        targetMarket
                        validDateFrom
                        allocationType
                        recommendedHoldingPeriod
                        recommendedHoldingPeriodTimeUnit
                        recommendedHoldingPeriodInYears
                        specificInvestmentNeedDetails {
                            need
                            applicability
                            targetMarket
                            validDateFrom
                            allocationType
                        }
                    }
                    distributionStrategies {
                        strategy
                        applicability
                        targetMarket
                        clientType
                        validDateFrom
                        allocationType
                    }
                }
            }
        }
    }
}`};

const mifidIICostAndChargesGraphQLQueryExample = {
    displayName: APIDisplayNames.MifidIICostAndCharges,
    query:`
query MiFIDCostAndChargesInstrument {
    instruments (
        ids: "10012767",
        scheme: VALOR
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            instrumentShortName
            instrumentStatus
            instrumentType
        }
        investorProtection{
            mifidIICostAndCharges {
                feeType
                referenceDate
                amount
                amountCurrency
                amountQuoteType
                maximumAmount
                maximumAmountCurrency
                maximumAmountQuoteType
                exPostReferencePeriodDateFrom
                exPostReferencePeriodDateTo
            }
        }
    }
}
    
query MiFIDCostAndChargesListing {
  listings (
        ids: "57399060_3231",
        scheme: VALOR_BC
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            listingShortName
            marketShortName
            listingCurrency
            listingStatus
            bc
        }
        investorProtection {
            mifidIICostAndCharges {
                feeType
                referenceDate
                amount
                amountCurrency
                amountQuoteType
                maximumAmount
                maximumAmountCurrency
                maximumAmountQuoteType
                exPostReferencePeriodDateFrom
                exPostReferencePeriodDateTo
            }
        }
    }
}`}

const issueConditionGraphQLQueryExample = {
    displayName: APIDisplayNames.IssueCondition,
    query:
`query IssueConditionByInstrument {
    instruments (
        scheme: VALOR,
        ids: ["155838577", "151517680", "139366857"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            instrumentShortName
            instrumentStatus
            instrumentType
        }
        referenceData {
            issueCondition {
                ipoFillingDate
                ipoTimingType
                ipoPeriodDateFrom
                issueStatus
                subscriptionType
                subscriptionDateFrom
                subscriptionDateTo
                subscriptionTime
                subscriptionCountry
                subscriptionLocation
                issuePaymentType
                accruedInterestDateFrom
                accruedInterestNumberOfDays
                placing
                issuePaymentDate
                issuePaymentDateFrom
                issuePaymentDateTo
                incomeEntitlementDateFrom
                issuePaymentOnSubscription
                minimumSubscription
                subscriptionIncrementAboveMinimumSubscription
                issueDate
                issueTime
                publicOfferingDetails {
                    country
                    investors {
                        investorGroup
                        dateFrom
                        dateTo
                    }
                }
                contactPerson
                contactPhoneNumber
                contactFaxNumber
                contactEmailAddress
                originalDenominations
                issuePrice
                issuePriceCurrency
                issuePriceQuoteType
                issuePriceRangeFrom
                issuePriceRangeTo
                issuePriceRangeCurrency
                issuePriceRangeQuoteType
                taxAmount
                taxCurrency
                taxType
                taxQuoteType
                numberOfIssuedInstruments
                issueAmount
                issueAmountCurrency
                issueAmountIsInfinite
                instrumentSalesRestrictions {
                    country
                    dateFrom
                    dateTo
                    minimumAmount
                    minimumAmountCurrency
                    maximumAmount
                    maximumAmountCurrency
                    investorRestrictions {
                        investorType
                        dateFrom
                        dateTo
                    }
                }
                partialIssuePayments {
                    date
                    partialAmount
                    partialAmountCurrency
                    partialRatio
                }
                tradingAnnouncements {
                    marketShortName
                    marketLongName
                    bc
                    mic
                    marketCountry
                    listingCurrency
                    ticker
                    firstTradingDate
                    lastTradingDate
                    lastTradingTime
                    smallestTradingUnit
                    isDirtyPrice
                }
                additionalRemark
            }
        }
    }
}

query IssueConditionByListing {
    listings (
        scheme: VALOR_BC,
        ids: ["155838577_67"]
    ) {
        requestedId
        requestedScheme
        lookupStatus
        lookup {
            listingShortName
            listingCurrency
            listingStatus
            marketShortName
        }
        referenceData {
            issueCondition {
                ipoFillingDate
                ipoTimingType
                ipoPeriodDateFrom
                issueStatus
                subscriptionType
                subscriptionDateFrom
                subscriptionDateTo
                subscriptionTime
                subscriptionCountry
                subscriptionLocation
                issuePaymentType
                accruedInterestDateFrom
                accruedInterestNumberOfDays
                placing
                issuePaymentDate
                issuePaymentDateFrom
                issuePaymentDateTo
                incomeEntitlementDateFrom
                issuePaymentOnSubscription
                minimumSubscription
                subscriptionIncrementAboveMinimumSubscription
                issueDate
                issueTime
                publicOfferingDetails {
                    country
                    investors {
                        investorGroup
                        dateFrom
                        dateTo
                    }
                }
                contactPerson
                contactPhoneNumber
                contactFaxNumber
                contactEmailAddress
                originalDenominations
                issuePrice
                issuePriceCurrency
                issuePriceQuoteType
                issuePriceRangeFrom
                issuePriceRangeTo
                issuePriceRangeCurrency
                issuePriceRangeQuoteType
                taxAmount
                taxCurrency
                taxType
                taxQuoteType
                numberOfIssuedInstruments
                issueAmount
                issueAmountCurrency
                issueAmountIsInfinite
                instrumentSalesRestrictions {
                    country
                    dateFrom
                    dateTo
                    minimumAmount
                    minimumAmountCurrency
                    maximumAmount
                    maximumAmountCurrency
                    investorRestrictions {
                        investorType
                        dateFrom
                        dateTo
                    }
                }
                partialIssuePayments {
                    date
                    partialAmount
                    partialAmountCurrency
                    partialRatio
                }
                tradingAnnouncements {
                    marketShortName
                    marketLongName
                    bc
                    mic
                    marketCountry
                    listingCurrency
                    ticker
                    firstTradingDate
                    lastTradingDate
                    lastTradingTime
                    smallestTradingUnit
                    isDirtyPrice
                }
                additionalRemark
            }
        }
    }
}`};

export const graphiQLQueryExamples = [
    defaultGraphQLQueryExample,
    instrumentBaseGraphQLQueryExample,
    entityBaseGraphQLQueryExample,
    listingBaseGraphQLQueryExample,
    marketBaseGraphQLQueryExample,
    instrumentSymbologyGraphQLQueryExample,
    listingSymbologyGraphQLQueryExample,
    instrumentMarketsGraphQLQueryExample,
    indexCompositionGraphQLQueryExample,
    instrumentClassificationGraphQLQueryExample,
    entityClassificationGraphQLQueryExample,
    instrumentDetailGraphQLQueryExample,
    marketHolidaysGraphQLQueryExample,
    issueConditionGraphQLQueryExample,
    mifidIISuitabilityGraphQLQueryExample,
    mifidIICostAndChargesGraphQLQueryExample,
    streamingMarketDataGraphQLQueryExample,
    intradaySnapshotGraphQLQueryExample,
    intradayHistoryGraphQLQueryExample,
    streamingIntradayTradesGraphQLQueryExample,
    endOfDaySnapshotGraphQLQueryExample,
    endOfDayHistoryGraphQLQueryExample,
    freeTextSearchGraphQLQueryExample,
    crireriaSearchGraphQLQueryExample,
    estimatesGraphQLQueryExample,
    fundamentalsGraphQLQueryExample,
    swissStampDutyGraphQLQueryExample
];
